/**
 * One rule, as the sentence the sheet prints — and which of its parts a trainer can press.
 *
 * The sheet does not put a family control above a rule; it states the rule as a sentence and the
 * family is a READING of that sentence rather than a label over it. That is why this returns TOKENS
 * instead of a string: three of them are controls, and a string would have to be parsed back to find
 * where they go.
 *
 * ── The ORDER is the mock's, and it is the reason this list exists ───────────────────────────
 * `Builder planu - modal - progresja`, `sentence3` (markup 598–614, script 3244–3258). One wrapping
 * row: the family chip, a joining word, the range and the column the client climbs, a second joining
 * word, the step. Which words appear depends on the state, and the mock's own three readings are:
 *
 * | State | The row |
 * |---|---|
 * | Statyczna | `co cykl zadany {kolumna} rośnie o` · PRZYROST |
 * | Od wyniku, niezwiązana | `gdy podopieczny dowiezie serię, zadany {kolumna} rośnie o` · PRZYROST |
 * | Od wyniku, związana | `gdy podopieczny dowiezie serię i przekroczy` · ZAKRES · KOLUMNA · `wtedy zadany {kolumna} rośnie o` · PRZYROST |
 * | Manualna | no row at all — the hint beside the chip is the whole statement |
 *
 * An earlier shape led with the condition and trailed a bound off the measurement dictionary
 * („do 300 kg"). Neither is in the mock, and the bound was a promise about a ceiling the trainer had
 * not authored.
 *
 * ── A text token's PARAMETERS are keys too ──────────────────────────────────────────────────
 * `model/` holds i18n keys and never resolved text, and `{{column}}` is a column NAME — which is a
 * key. So `paramKeys` travels and the renderer resolves both levels. Handing a resolved name back
 * would mean `t` living here, which is the one thing this segment may not hold.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import {
  axisBaseOf,
  bandOf,
  bandedRuleColumns,
  carriesProgression,
  countsAsWork,
  measurementValueKey,
  resolveTopIndex,
  type ExerciseDraft,
  type ProgressionAxis,
  type ProgressionBand,
  type ProgressionRule,
} from '~/entities/plan-draft';
import { measurementFieldDefinition, type MeasurementFieldKey } from '~/shared/measurement';

import { formatNumber } from './matrixCellState';

/** How the rule's numbers are known — the sheet's dot colour and its „liczby pewne" / „szacunek" note. */
export type SentenceFamily = 'static' | 'fromResult';

/**
 * A part of the sentence the trainer presses rather than reads.
 *
 * The control words itself from the staged exercise — the stepper from the rule's own delta, the
 * range from the rows, the column from the rule's `banded` field — so what travels is WHICH control
 * belongs where, never a copy of the number it is about.
 */
export type SentenceControl = 'delta' | 'range' | 'gateColumn';

export type SentenceToken =
  | {
      readonly kind: 'text';
      readonly key: string;
      /** Interpolations whose VALUES are themselves keys. See the header. */
      readonly paramKeys?: Readonly<Record<string, string>>;
    }
  | { readonly kind: 'control'; readonly control: SentenceControl };

export interface RuleSentence {
  readonly family: SentenceFamily;
  readonly tokens: readonly SentenceToken[];
}

const KEY = 'web:planBuilder.progression.sentence';
const JOIN = 'web:planBuilder.progression.sheet.join';

/** The MINUS SIGN (U+2212), not a hyphen — a signed delta is a number, and a hyphen reads as a dash. */
const MINUS = '−';

/**
 * The i18n key naming a column inside a sentence („ciężar", „powtórzenia").
 *
 * On a back-off block the load column names the TOP row it belongs to — „ciężar serii głównej" —
 * because the descents underneath take a percentage of it rather than the rule's step.
 */
export function columnNounKey(field: MeasurementFieldKey, hasTopSet: boolean): string {
  return `${KEY}.subject.${field === 'weight' && hasTopSet ? 'topWeight' : field}`;
}

/**
 * The gated column as the SUBJECT of a clause — „liczba powtórzeń wraca na 8", „czas wraca na 30".
 *
 * A second table rather than `columnNounKey`'s, because a noun that reads correctly after „zadany"
 * does not read correctly in front of a verb: „powtórzenia wraca" is not Polish, and the count of
 * them is what goes back to the floor rather than the repetitions themselves. Every column has an
 * entry, so the clause holds on time, distance and rounds as well as on repetitions.
 */
export function gateSubjectKey(field: MeasurementFieldKey): string {
  return `web:planBuilder.progression.sheet.gateSubject.${field}`;
}

/**
 * What the two ends of a band are COUNTED IN, agreeing with the number in front of them —
 * „8–12 powtórzeń", „2–4 powtórzenia", „30–45 s", „7–9 RPE".
 *
 * Polish inflects a noun after a numeral, and the phrase read „przy 8–12 powtórzenia" until
 * 14 September 2026: a nominative plural glued onto a range, after a preposition that takes neither.
 * The forms come from `plans.progression.noun.*` — the VERBATIM bundle, the mobile app's own table —
 * rather than from a second one written here, and the CEILING is the number they agree with because
 * it is the one the reader has just read.
 *
 * A column the table has no noun for is counted in its dictionary unit, which is what its numbers
 * are written in anyway. Only `weight`, `duration`, `distance` and `speed` take that branch, and all
 * four have a unit.
 */
const COUNTED_NOUNS: readonly MeasurementFieldKey[] = ['reps', 'rounds', 'rpe', 'rir', 'tempo'];

export function bandCountText(
  field: MeasurementFieldKey,
  ceiling: number,
  t: (key: string, values?: Record<string, string | number>) => string,
): string {
  if (COUNTED_NOUNS.includes(field)) {
    return t(`plans.progression.noun.${field}`, { count: ceiling });
  }
  return measurementFieldDefinition(field).unit ?? '';
}

/**
 * WHICH OF THE THREE NOTATIONS a step is written in — a compounding percent, percentage points of
 * the client's max, or the column's own unit.
 *
 * The unit is the STEP's, not the column's, and the two differ in exactly one place: on a load
 * priced as a fraction of the max, a step of 2,5 is two and a half PERCENTAGE POINTS, not two and a
 * half kilograms. A `Percent` delta compounds the reference instead of adding to it, and reads as a
 * plain percent whatever column it sits on.
 */
export type DeltaNotation = 'percent' | 'points' | 'columnUnit';

export function deltaNotation(
  field: MeasurementFieldKey,
  rule: ProgressionRule,
  isPercentPriced: boolean,
): DeltaNotation {
  if (rule.deltaUnit === 'Percent') return 'percent';
  if (field === 'weight' && isPercentPriced) return 'points';
  return 'columnUnit';
}

/** The pill's text for a delta, in whichever of the three notations `deltaNotation` names. */
export function formatRuleDelta(
  field: MeasurementFieldKey,
  rule: ProgressionRule,
  delta: number,
  isPercentPriced: boolean,
  locale: string,
): string {
  const sign = delta < 0 ? MINUS : '+';
  const magnitude = formatNumber(Math.abs(delta), locale);
  const notation = deltaNotation(field, rule, isPercentPriced);
  if (notation === 'percent') return `${sign}${magnitude} %`;
  if (notation === 'points') return `${sign}${magnitude} pp`;
  const { unit } = measurementFieldDefinition(field);
  return unit === null ? `${sign}${magnitude}` : `${sign}${magnitude} ${unit}`;
}

/** What the sentence needs: the exercise's rows, its load pricing, and its measurement contract. */
export type SentenceSource = Pick<
  ExerciseDraft,
  'sets' | 'progressionRules' | 'prescriptionMode' | 'measurementFields'
>;

/**
 * The band's ceiling as the SENTENCE and the CHART state it — the first banded set's.
 *
 * The sheet writes one ceiling across every banded row, so they normally agree; where a trainer has
 * since differentiated them row by row, the rule still names ONE bar and the MATRIX is what shows
 * each row's own. A sentence that tried to name several would stop being a sentence, and a chart
 * that did would stop being a line.
 *
 * Exported because the trajectory preview asks the same question, and two answers to "which ceiling
 * is the rule's" is how a chart comes to disagree with the words above it.
 */
export function sentenceCeiling(
  exercise: Pick<ExerciseDraft, 'sets'>,
  band: ProgressionBand | null,
): number | null {
  if (band === null) return null;
  for (const set of exercise.sets) {
    const ceiling = band.ceilingBySetLocalId.get(set.localId);
    if (ceiling !== undefined) return ceiling;
  }
  return null;
}

/**
 * The sentence one rule reads as, or null when there is no rule to read.
 *
 * NULL COVERS A FLAT `Static` RULE TOO. A rule with no delta expands the authored base unchanged
 * across the phase, which is the same plan as no rule at all — and the backend hands one back on
 * WEIGHT for every exercise nobody authored a progression for, including movements that measure no
 * load. Rendering that artefact as a sentence would put „ciężar · każdy cykl" on a push-up.
 */
export function ruleSentence(
  exercise: SentenceSource,
  field: MeasurementFieldKey,
  rule: ProgressionRule | null,
): RuleSentence | null {
  if (rule === null || !carriesProgression(rule)) return null;

  // BANDED IS ABOUT THE RULE, NOT ABOUT THE ROWS — `bandedRuleColumns` rather than `bandOf`, and
  // that distinction is exactly what the two functions exist for. The ceiling is the number the
  // trainer is about to type, so a sentence that waited for one drew no field to type it into: the
  // band switch went on and nothing appeared.
  const banded =
    rule.type === 'PreviousExecution' ? bandedRuleColumns(exercise.progressionRules) : null;
  const column = { column: columnNounKey(field, resolveTopIndex(exercise.sets) !== -1) };
  const tokens: SentenceToken[] = [];

  if (banded === null) {
    tokens.push({
      kind: 'text',
      key: rule.type === 'Static' ? `${JOIN}.static` : `${JOIN}.fromResult`,
      paramKeys: column,
    });
  } else {
    tokens.push({ kind: 'text', key: `${JOIN}.fromResultBanded` });
    tokens.push({ kind: 'control', control: 'range' });
    tokens.push({ kind: 'control', control: 'gateColumn' });
    tokens.push({ kind: 'text', key: `${JOIN}.thenGrows`, paramKeys: column });
  }

  if (rule.delta !== null) tokens.push({ kind: 'control', control: 'delta' });

  return { family: rule.type === 'Static' ? 'static' : 'fromResult', tokens };
}

/**
 * The band the CHART runs against: the bar the rule names, and the row prescription it climbs from.
 *
 * The FLOOR is a row's ordinary prescription on the gated column and the CEILING is the rule's, so
 * the pair is read off the first row that carries both. It lived in `TrajectoryPreview` as a
 * component-local helper, which put a decision the chart's whole shape depends on somewhere no test
 * could reach; `sentenceCeiling` already answered half of it one file away.
 */
export function sentenceBand(
  exercise: Pick<ExerciseDraft, 'sets' | 'progressionRules'>,
): { readonly floor: number; readonly ceiling: number } | null {
  const band = bandOf(exercise);
  if (band === null) return null;
  const valueKey = measurementValueKey(band.gatedField);
  if (valueKey === null) return null;
  for (const set of exercise.sets) {
    const ceiling = band.ceilingBySetLocalId.get(set.localId);
    if (ceiling === undefined) continue;
    const floor = set[valueKey];
    if (typeof floor !== 'number') continue;
    return { floor, ceiling };
  }
  return null;
}

/**
 * The base a run STARTS from: the first row that COUNTS and prices this column.
 *
 * A warm-up's number is not what the rule advances, and a movement whose working rows carry nothing
 * on the column has no trajectory at all — drawing one from zero would be a number nobody wrote.
 */
export function sentenceBase(
  sets: readonly ExerciseDraft['sets'][number][],
  axis: ProgressionAxis,
): number | null {
  return (
    sets
      .filter((set) => countsAsWork(set.setType))
      .map((set) => axisBaseOf(set, axis))
      .find((value) => value !== null) ?? null
  );
}
